import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { TFunction } from 'i18next';
import styled, { css } from 'styled-components';
import { asHtml } from '../../../helpers/markdown';
import QueryFilter from '../../../helpers/query-filter';
import { slugify } from '../../../helpers/urls';
import type { RootState } from '../../../reducers';
import Content from '../../../styled-components/components/Content';
import { H3 } from '../../../styled-components/components/Heading';
import { IconMinus, IconPlus } from '../../../styled-components/components/Icon';
import { IconBackdrop } from '../../../styled-components/components/IconBackdrop';
import { LinkButton } from '../../../styled-components/components/LinkButton';
import { SrOnly } from '../../../styled-components/components/SrOnly';
import { size } from '../../../styled-components/layout/helpers';
import { getLocationSet } from '../../../utils/maps';
import { type CombinedFacet, getFacetList, getQueryFilter } from '../../../utils/reselectors';
import useSeoSearch from '../../customer-service/Data/useSeoSearch';

const HiddenParams = ['isAuctionItem', 'sellerType', 'leasing', 'businessLeasing', 'tags'];
const BlockingFacets = [
  'isAuctionItem',
  'sellerType',
  'tags',
  'environmentClass',
  'branches',
  'price',
  'leasingPrice',
  'businessLeasingPrice',
  'mileage',
  'properties.seats',
  'properties.co2',
  'enginePower',
  'hasManufacturerPackaging',
  'isAuthorizedResellerForManufacturer',
  'deductibleVat',
  'mrf',
  'hasMedia',
  'ecom',
];

const FacetSortOrder = {
  manufacturer: 1,
  modelSeries: 2,
};

const casing = (raw: string) =>
  raw.length > 2 && raw[1].toUpperCase() === raw[1] ? raw : raw.toLowerCase();

const getFacetSortOrder = (keyName: string) =>
  FacetSortOrder[keyName as 'manufacturer' | 'modelSeries'] || 999;

const createIngress = (
  headerNames: { [key: string]: string | undefined },
  valueNames: { [key: string]: string | undefined },
  spatialName: string | null,
  t: TFunction,
) => {
  const locations = getLocationSet().reduce(
    (all, next) => {
      const key = slugify(next.name);
      all[key] = next.name;

      return all;
    },
    {} as { [key: string]: string | undefined },
  );

  const formatter = {
    manufacturer: (token: string) => token,
    modelSeries: (token: string) => token,
    modelYear: (token: string) => token,
    'position.name': (token: string) => token,
    gearboxType: (token: string) =>
      token.toLowerCase() === 'automat' ? t('automatic', { ns: 'search' }) : token.toLowerCase(),
    fuelType: (token: string) => token.toLowerCase(),
    chassis: (token: string) => token.toLowerCase(),
    drivingWheel: (token: string) => token.toLowerCase(),
    segment: (token: string) => token.toLowerCase().replace('suv', 'SUV').replace('mpv', 'MPV'),
    'properties.segment': (token: string) =>
      token.toLowerCase().replace('suv', 'SUV').replace('mpv', 'MPV'),
    color: (token: string) => token.toLowerCase(),
  };

  const templates = {
    manufacturer: (token: string) =>
      ` ${t('from manufacturer', { ns: 'search', name: formatter.manufacturer(token) })}`,
    modelSeries: (token: string) =>
      ` ${t('in the model', { ns: 'search', name: formatter.modelSeries(token) })}`,
    modelYear: (token: string) =>
      `, ${t('specific model year', { ns: 'search', name: formatter.modelYear(token) || t('unknown', { ns: 'validation' }) })}`,
    'position.name': (token: string) =>
      ` ${t('in a location and surroundings', { ns: 'search', location: locations[token] })}`,
    gearboxType: (token: string) =>
      ` ${t('with specific gearbox', { ns: 'search', name: formatter.gearboxType(token) })}`,
    fuelType: (token: string) => ` ${formatter.fuelType(token)}`,
    chassis: (token: string) => ` ${formatter.chassis(token)}`,
    drivingWheel: (token: string) =>
      ` ${t('with driving wheel', { ns: 'search', name: formatter.drivingWheel(token) })}`,
    segment: (token: string) => token.toLowerCase().replace('suv', 'SUV').replace('mpv', 'MPV'),
    'properties.segment': (token: string) =>
      ` ${t('in the segment', { ns: 'search', name: formatter.segment(token) })}`,
    color: (token: string) =>
      ` ${t('in the color', { ns: 'search', name: formatter.color(token) })}`,
  };

  type TemplatesKeys = keyof typeof templates;

  const prefix = Object.keys(headerNames).includes('fuelType')
    ? t('Definite cars', { ns: 'search', name: formatter.fuelType(headerNames.fuelType as string) })
    : t('cars', { ns: 'search' });

  const value = Object.keys(headerNames)
    .filter((h) => h !== 'fuelType' && !!templates[h as TemplatesKeys])
    .map((h) => templates[h as TemplatesKeys](headerNames[h] as string));

  const suffix = Object.keys(valueNames)
    .filter((x) => !!templates[x as TemplatesKeys])
    .map((x) => templates[x as TemplatesKeys](valueNames[x] as string));

  let spatial = '';
  if (spatialName) spatial = `, ${t('in and around', { ns: 'search', name: spatialName })}`;

  if (suffix.length) {
    return t('You will find this car at us', {
      ns: 'search',
      name: `${prefix}${value}${suffix}${spatial}`,
    });
  }

  return t('You will find new and used cars at us', {
    ns: 'search',
    name: `${prefix}${value}${suffix}${spatial}`,
  });
};

const getFullFilterIngress = (queryFilter: QueryFilter, facets: CombinedFacet[], t: TFunction) => {
  const manufacturer = queryFilter.get('manufacturer').pop();
  const model = queryFilter.get('modelSeries').pop();
  const modelPopularity =
    (facets
      .find((f) => f.id === 'modelSeries')
      ?.contextual?.filters?.slice()
      .filter((f) => f.count > 0)
      .findIndex((f) => f.displayName === model) || 0) + 1;

  const mostPopularChassis = facets
    .find((f) => f.id === 'chassis')
    ?.filters.slice()
    .sort((a, b) => a.count - b.count)
    .pop()?.displayName;
  const fuelTypes = facets
    .find((f) => f.id === 'fuelType')
    ?.filters.slice()
    .sort((a, b) => a.count - b.count)
    .filter((f) => f.count > 0);
  const mostPopularFuelType = fuelTypes?.pop()?.displayName;
  const leastPopularFuelType = fuelTypes?.length ? fuelTypes?.[0].displayName : null;
  const vehicleCount = facets
    .find((f) => f.id === 'modelSeries')
    ?.filters.slice()
    .find((f) => f.displayName === model)?.count;
  const colors = facets
    .find((f) => f.id === 'color')
    ?.filters.slice()
    .sort((a, b) => b.count - a.count)
    .filter((f) => f.count > 0)
    .map((f) => f.displayName)
    .filter((f, index, self) => self.indexOf(f) === index)
    .slice(0, 3)
    .reverse()
    .join(', ')
    .replace(/,([^,]*)$/, ` ${t('and', { ns: 'search' })}$1`);

  const avgYear = facets
    .find((f) => f.id === 'modelYear')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.years +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, years: 0 },
    );
  const avgMilage = facets
    .find((f) => f.id === 'mileage')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.milage +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, milage: 0 },
    );
  const avgPrice = facets
    .find((f) => f.id === 'price')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.price +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, price: 0 },
    );

  if (leastPopularFuelType) {
    return t('Information about this model on this site with multiple fuel types.', {
      ns: 'search',
      manufacturer,
      model,
      chassis: casing(mostPopularChassis || ''),
      popularity: t('most popular', { ns: 'search', count: modelPopularity }),
      vehicleCount,
      colors: colors?.toLowerCase(),
      fuelType: mostPopularFuelType?.toLowerCase(),
      fuelTypeAlt: leastPopularFuelType.toLowerCase(),
      year: Math.round((avgYear?.years || 0) / (avgYear?.documents || 0)),
      mileage: Math.round((avgMilage?.milage || 0) / (avgMilage?.documents || 0)),
      price: Math.round((avgPrice?.price || 0) / (avgPrice?.documents || 0)),
    });
  }

  return t('Information about this model on this site.', {
    ns: 'search',
    manufacturer,
    model,
    chassis: casing(mostPopularChassis || ''),
    popularity: t('most popular', { ns: 'search', count: modelPopularity }),
    vehicleCount,
    colors: colors?.toLowerCase(),
    fuelType: mostPopularFuelType?.toLowerCase(),
    year: Math.round((avgYear?.years || 0) / (avgYear?.documents || 0)),
    mileage: Math.round((avgMilage?.milage || 0) / (avgMilage?.documents || 0)),
    price: Math.round((avgPrice?.price || 0) / (avgPrice?.documents || 0)),
  });
};

const getManufacturerIngress = (
  queryFilter: QueryFilter,
  facets: CombinedFacet[],
  t: TFunction,
) => {
  const manufacturer = queryFilter.get('manufacturer').pop();
  const vehicleCount = facets
    .find((f) => f.id === 'manufacturer')
    ?.filters.slice()
    .find((f) => f.displayName === manufacturer)?.count;
  const fuelTypes = facets
    .find((f) => f.id === 'fuelType')
    ?.filters.slice()
    .sort((a, b) => a.count - b.count)
    .filter((f) => f.count > 0);
  const mostPopularFuelType = fuelTypes?.pop()?.displayName;
  const leastPopularFuelType = fuelTypes?.length ? fuelTypes?.[0].displayName : null;
  const colors = facets
    .find((f) => f.id === 'color')
    ?.filters.slice()
    .sort((a, b) => b.count - a.count)
    .filter((f) => f.count > 0)
    .map((f) => f.displayName)
    .filter((f, index, self) => self.indexOf(f) === index)
    .slice(0, 3)
    .reverse()
    .join(', ')
    .replace(/,([^,]*)$/, ` ${t('and', { ns: 'search' })}$1`);

  const avgYear = facets
    .find((f) => f.id === 'modelYear')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.years +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, years: 0 },
    );
  const avgMilage = facets
    .find((f) => f.id === 'mileage')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.milage +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, milage: 0 },
    );
  const avgPrice = facets
    .find((f) => f.id === 'price')
    ?.contextual?.filters?.reduce(
      (agg, next) => {
        agg.documents += next.count;
        agg.price +=
          Number.parseInt(next.displayName.replace(/^[^0-9]|[^0-9]$/g, ''), 10) * next.count;
        return agg;
      },
      { documents: 0, price: 0 },
    );

  if (leastPopularFuelType) {
    return t('Information about this vehicle on this site with multiple fuel types.', {
      ns: 'search',
      manufacturer,
      vehicleCount,
      colors: colors?.toLowerCase(),
      fuelType: mostPopularFuelType?.toLowerCase(),
      fuelTypeAlt: leastPopularFuelType.toLowerCase(),
      year: Math.round((avgYear?.years || 0) / (avgYear?.documents || 0)),
      mileage: Math.round((avgMilage?.milage || 0) / (avgMilage?.documents || 0)),
      price: Math.round((avgPrice?.price || 0) / (avgPrice?.documents || 0)),
    });
  }

  return t('Information about this vehicle on this site.', {
    ns: 'search',
    manufacturer,
    vehicleCount,
    colors: colors?.toLowerCase(),
    fuelType: mostPopularFuelType?.toLowerCase(),
    year: Math.round((avgYear?.years || 0) / (avgYear?.documents || 0)),
    mileage: Math.round((avgMilage?.milage || 0) / (avgMilage?.documents || 0)),
    price: Math.round((avgPrice?.price || 0) / (avgPrice?.documents || 0)),
  });
};

const getIngress = (
  headerNames: { [key: string]: string | undefined },
  valueNames: { [key: string]: string | undefined },
  spatialName: string | null,
  queryFilter: QueryFilter,
  facets: CombinedFacet[],
  t: TFunction,
) => {
  try {
    if (
      queryFilter.numberOfFilters() === 2 &&
      queryFilter.has('manufacturer') &&
      queryFilter.has('modelSeries') &&
      !queryFilter.get('manufacturer').pop()?.includes('exclude:') &&
      !queryFilter.get('modelSeries').pop()?.includes('exclude:')
    )
      return getFullFilterIngress(queryFilter, facets, t);

    if (
      queryFilter.numberOfFilters() === 1 &&
      queryFilter.has('manufacturer') &&
      !queryFilter.get('manufacturer').pop()?.includes('exclude:')
    )
      return getManufacturerIngress(queryFilter, facets, t);
  } catch (_e) {} // eslint-disable-line no-empty

  return createIngress(headerNames, valueNames, spatialName, t);
};

const SeoIngressWrapper = styled.div.withConfig({ componentId: 'sc-1kxzwie-0' })`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${size(1)};
`;

interface SeoIngressTruncateProps {
  $truncate?: boolean;
}

const SeoIngressTruncate = styled.div.withConfig({ componentId: 'sc-1kxzwie-1' })<SeoIngressTruncateProps>`
    ${({ $truncate }) =>
      $truncate &&
      css`
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        `}
`;

interface SeoIngressProps {
  __html: string;
}

const SeoIngress = ({ __html }: SeoIngressProps) => {
  const { t } = useTranslation('common');
  const [extended, setExtended] = useState(false);

  return (
    <>
      {__html.length > 400 ? (
        <SeoIngressWrapper>
          <SeoIngressTruncate $truncate={!extended}>
            <Content fontSize="small" dangerouslySetInnerHTML={{ __html }} />
          </SeoIngressTruncate>
          <LinkButton
            onClick={() => setExtended(!extended)}
            title={extended ? t('showLess', { ns: 'common' }) : t('showMore', { ns: 'common' })}
            $size="small"
          >
            {extended ? t('showLess', { ns: 'common' }) : t('showMore', { ns: 'common' })}
            <IconBackdrop $size="small" $tone="accent" aria-hidden="true">
              {extended ? <IconMinus /> : <IconPlus />}
            </IconBackdrop>
          </LinkButton>
        </SeoIngressWrapper>
      ) : (
        <Content fontSize="small" dangerouslySetInnerHTML={{ __html }} />
      )}
    </>
  );
};

const SearchEngineOptimizationBlock = () => {
  const seoOverride = useSeoSearch();
  const { t } = useTranslation('search');
  const isSearchEngineOptimized = useSelector<RootState, boolean>((s) => s.search.initialized);
  const _queryFilter = useSelector(getQueryFilter);
  const facets = useSelector(getFacetList);

  let shouldBlock = _queryFilter.hasQuery();
  _queryFilter.getKeys().forEach((key) => {
    if (BlockingFacets.includes(key) || _queryFilter.get(key).length > 1) shouldBlock = true;
  });

  const queryFilter = _queryFilter.removeAll(QueryFilter.OperationalParams.concat(HiddenParams));

  if (
    shouldBlock ||
    !isSearchEngineOptimized ||
    queryFilter.isEmpty() ||
    !queryFilter.isSame(_queryFilter, false)
  ) {
    // Render default (hidden) h1 if no SEO data is available
    return <SrOnly as="h1">{t('findVehicles', { ns: 'search' })}</SrOnly>;
  }

  if (seoOverride) {
    const seoIngress = seoOverride.ingress ? asHtml(seoOverride.ingress) : undefined;

    return (
      <div className="m-t-double">
        <H3 as="h1">{seoOverride.title}</H3>
        {seoIngress && <SeoIngress __html={seoIngress} />}
      </div>
    );
  }

  const keys = queryFilter
    .removeSpatial()
    .getKeys()
    .sort((a, b) => getFacetSortOrder(a) - getFacetSortOrder(b));
  const headerKeyNames = keys.reduce(
    (agg, next) => {
      const value = queryFilter.get(next).pop();
      if (value) {
        agg[next] = value;
      }
      return agg;
    },
    {} as { [key: string]: string | undefined },
  );
  const valueKeyNames = keys.reduce(
    (agg, next) => {
      if (queryFilter.hasRange(next)) {
        const max = queryFilter.getRangeMax(next);
        const min = queryFilter.getRangeMin(next);
        const f = facets.find((x) => x.id === next);
        const spanMin = f?.filters?.[0];
        const spanMax = f?.filters?.[f.filters.length - 1];
        if (min !== undefined && max !== undefined) {
          agg[next] = `${min}–${max}`;
        } else if (min !== undefined) {
          agg[next] = `${min}–${spanMax?.displayName}`;
        } else if (max !== undefined) {
          agg[next] = `${spanMin?.displayName}–${max}`;
        }

        //agg[next] = queryFilter.getRangeMax(next);
      }
      return agg;
    },
    {} as { [key: string]: string | undefined },
  );

  const headerNames = Object.keys(headerKeyNames)
    .filter((key) => !BlockingFacets.includes(key))
    .map((key) => headerKeyNames[key])
    .join(' ')
    .replace(/^\s+|\s+$/g, '');
  let header = t('New and used from this manufacturer', {
    ns: 'search',
    name: headerNames || t('cars', { ns: 'search' }),
  });
  let spatialName = null;

  if (queryFilter.hasSpatial()) {
    const spatial = queryFilter.getSpatialName();
    const ua = getLocationSet()
      .filter((u) =>
        spatial
          .toLocaleLowerCase()
          .replace(/[^a-z]/g, '')
          .includes(u.name.toLocaleLowerCase().replace(/[^a-z]/g, '')),
      )
      .pop();
    if (ua) {
      spatialName = ua.name;
      header += ` ${t('in this location', { ns: 'search', location: ua.name })}`;
    }
  }

  return (
    <div className="m-t-double">
      <H3 as="h1">{header}</H3>
      <Content fontSize="small">
        <p>{getIngress(headerKeyNames, valueKeyNames, spatialName, queryFilter, facets, t)}</p>
      </Content>
    </div>
  );
};

export default SearchEngineOptimizationBlock;
